import React from "react"
import { useProjectStore } from "stores/projectStore"

import { useProjectTypeQuery } from "features/Projects/api/useProjectsQuery"
import { useUnitUsersQuery } from "hooks/api/useUnitsQuery"
import ProjectDetailsTabs from "./ProjectDetailsTabs.container"
import ProjectDetailsHeader from "features/ProjectDetails/components/ProjectDetailsHeader/ProjectDetailsHeader.component"
import { PROJECT_TYPE } from "types/project.types"

// Define the props interface
interface ProjectDetailsContainerProps {
  refreshProject: () => void
}

/**
 * ProjectDetailsContainer component - Displays the project details along with navigation breadcrumbs.
 *
 * This component is responsible for rendering the overall layout of the project details page. It includes
 * navigation breadcrumbs at the top for easy navigation, the project's status, and the project's name in a
 * prominent position. The `ProjectDetailsTabs` component is used to display detailed sections of the project
 * information below the header.
 *
 * The layout is structured with a top bar containing the breadcrumbs and the project status, followed by the
 * project name. The `ProjectBreadcrumb` component is used to render the navigation breadcrumbs, and the
 * `ProjectStatus` component displays the current status of the project. The project name is displayed as a
 * large header, emphasizing the focus of the page.
 *
 * @component
 * @param props - The component props.
 * @returns The rendered ProjectDetailsContainer component, including breadcrumbs, status, and project name.
 */
const ProjectDetailsContainer: React.FC<ProjectDetailsContainerProps> = ({
  refreshProject,
}) => {
  const project = useProjectStore((state) => state.project)

  // Sequence errors are silent by design (query falls back to DEFAULT);
  // transport errors notify via the queries' errorTitle meta.
  const typeQuery = useProjectTypeQuery(project.id)
  const unitUsersQuery = useUnitUsersQuery()

  const projectType = typeQuery.data ?? PROJECT_TYPE.DEFAULT
  const userData = unitUsersQuery.data ?? []

  return (
    <div id="project-details" className="flex flex-col px-4 py-3 h-full">
      {/* One line: breadcrumb with the project name, status, actions */}
      <div className="mb-3">
        <ProjectDetailsHeader userData={userData} />
      </div>
      <div className="flex-1 min-h-0 overflow-y-hidden">
        {/* The sections; the sidebar switches between them */}
        <ProjectDetailsTabs
          projectType={projectType}
          refreshProject={refreshProject}
        />
      </div>
    </div>
  )
}

export default ProjectDetailsContainer
